import { useState, useEffect, useMemo, useRef } from 'react';
import type { RosterEntry, Overlap, User, Meetup, MeetupRsvp } from '../../types';
import { getCityName } from '../../utils/airports';
import { getAirline } from '../../utils/airlines';
import { getFriendsInCity } from '../../utils/overlaps';
import { useApp } from '../../context/AppContext';
import { parseISO, startOfMonth, getDay, format } from 'date-fns';
import { toDateStr } from '../../utils/dates';
import { Plane, MapPin, Coffee, Trash2, Pencil, X, Users, Crown, MessageCircle, Home, CalendarCheck, Clock, ThumbsUp, ThumbsDown, Share2 } from 'lucide-react';
import type { Flight } from '../../types';
import { haptic, hapticMedium } from '../../utils/haptics';
import { shareText } from '../../utils/nativeShare';
import { formatDateShort } from '../../utils/dates';
import { DOWNLOAD_URL } from '../../constants/links';
import { PremiumUpsellSheet } from '../premium/PremiumUpsellSheet';
import { NudgeSheet } from './NudgeSheet';
import { FlightMatchSheet } from './FlightMatchSheet';
import { QRModal } from '../shared/QRModal';
import type { CityOverlapGroup, FriendStay } from '../../utils/overlapGroups';
import { AvatarImg } from '../shared/Avatar';
import { FriendProfileSheet } from '../profile/FriendProfileSheet';
import { ChatView } from '../chat/ChatView';
import { useNavigate } from 'react-router-dom';
import { useFlightMatchCounts } from '../../hooks/useFlightMatchCounts';
import { track } from '../../analytics';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

interface MonthGridProps {
  roster: RosterEntry[];
  overlaps: Overlap[];
  year: number;
  month: number;
  onEditFlight?: (flight: Flight) => void;
  cityGroups?: CityOverlapGroup[];
  currentUser?: User;
  rosterActive?: boolean;
  premiumGatedOverlap?: (date: string) => boolean;
  /** Externally triggered date selection (from Upcoming Overlaps list) */
  externalSelectedDate?: string | null;
  onExternalDateConsumed?: () => void;
}

export function MonthGrid({
  roster, overlaps, year, month, onEditFlight,
  cityGroups = [], currentUser, rosterActive = true, premiumGatedOverlap,
  externalSelectedDate, onExternalDateConsumed,
}: MonthGridProps) {
  const { removeFlight, meetups, meetupRsvps, rsvpMeetup, cancelMeetup, currentUser: ctxUser, usersById, groupMembers, flights: userFlights } = useApp();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [tappedDate, setTappedDate] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [nudgeGroup, setNudgeGroup] = useState<CityOverlapGroup | null>(null);
  const [nudgeInitialDate, setNudgeInitialDate] = useState<string | undefined>();
  const [flightMatchFlight, setFlightMatchFlight] = useState<Flight | null>(null);
  const [showQRFromMatch, setShowQRFromMatch] = useState(false);
  const trackedMatchesRef = useRef<Set<string>>(new Set());

  // Flight match counts — anonymous count of non-friend R&F users per flight
  const flightMatchCounts = useFlightMatchCounts(userFlights, month, year);

  // Track flight match impressions (deduped per session)
  useEffect(() => {
    if (!selectedDate) return;
    const entry = rosterMap.get(selectedDate);
    if (entry?.type !== 'flight' || !entry.flight) return;
    const key = `${entry.flight.flightNumber}|${entry.flight.date}`;
    const count = flightMatchCounts.get(key);
    if (count && count > 0 && !trackedMatchesRef.current.has(key)) {
      trackedMatchesRef.current.add(key);
      track('flight_match_seen', { flight_number: entry.flight.flightNumber, date: entry.flight.date, count });
    }
  }, [selectedDate, flightMatchCounts]);

  // Handle external date selection from Upcoming Overlaps
  useEffect(() => {
    if (externalSelectedDate) {
      setSelectedDate(externalSelectedDate);
      onExternalDateConsumed?.();
    }
  }, [externalSelectedDate, onExternalDateConsumed]);

  // Turnaround dates — dates with 2+ flights (same-day out & back)
  const turnaroundDates = useMemo(() => {
    const dateCounts = new Map<string, number>();
    for (const f of userFlights) {
      dateCounts.set(f.date, (dateCounts.get(f.date) || 0) + 1);
    }
    const set = new Set<string>();
    for (const [date, count] of dateCounts) {
      if (count >= 2) set.add(date);
    }
    return set;
  }, [userFlights]);

  // Active group IDs — groups where user has non-deleted membership
  const activeGroupIds = useMemo(() => {
    const uid = ctxUser?.id;
    if (!uid) return new Set<string>();
    return new Set(
      groupMembers.filter((m) => m.userId === uid && !m.deletedAt).map((m) => m.groupId),
    );
  }, [groupMembers, ctxUser?.id]);

  // Meetup dates for this month — non-cancelled, future, in active groups
  const meetupDateSet = useMemo(() => {
    const todayStr = toDateStr(new Date());
    const set = new Set<string>();
    for (const m of meetups) {
      if (m.status !== 'cancelled' && m.date >= todayStr && activeGroupIds.has(m.groupChatId)) set.add(m.date);
    }
    return set;
  }, [meetups, activeGroupIds]);

  // Meetups for the selected date
  const selectedDateMeetups = useMemo(() => {
    if (!selectedDate) return [];
    return meetups.filter((m) => m.date === selectedDate && m.status !== 'cancelled' && activeGroupIds.has(m.groupChatId));
  }, [meetups, selectedDate, activeGroupIds]);

  const rosterMap = new Map<string, RosterEntry>();
  for (const entry of roster) {
    rosterMap.set(entry.date, entry);
  }

  const hasFlights = roster.some(e => e.type === 'flight' || e.type === 'layover');

  const today = toDateStr(new Date());

  const firstDay = startOfMonth(new Date(year, month - 1));
  const startDow = (getDay(firstDay) + 6) % 7;

  const cells: (RosterEntry | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (const entry of roster) cells.push(entry);

  const weeks: (RosterEntry | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  const lastWeek = weeks[weeks.length - 1];
  while (lastWeek.length < 7) lastWeek.push(null);

  const selectedEntry = selectedDate ? rosterMap.get(selectedDate) : null;

  // Find the CityOverlapGroup that contains the selected date.
  // On flight days, skip home-base groups — show flight detail instead.
  const isFlightDay = selectedEntry?.type === 'flight';
  const selectedOverlapGroup = selectedDate
    ? findOverlapGroupForDate(selectedDate, cityGroups, isFlightDay, currentUser?.baseAirport)
    : null;

  const handleCellTap = (date: string) => {
    haptic();
    setTappedDate(date);
    setTimeout(() => setTappedDate(null), 150);
    setConfirmDelete(false);
    setSelectedDate(selectedDate === date ? null : date);
  };

  return (
    <div className="px-3 pt-2 pb-3">
      {/* Grid */}
      <div className="bg-slate-800 rounded-xl p-2 border border-slate-700/50">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-[2px] mb-0.5">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-slate-500 py-0.5">
              {d}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-[2px]">
            {week.map((entry, di) => {
              if (!entry) {
                return <div key={`empty-${di}`} className="aspect-square" />;
              }

              const dayNum = parseISO(entry.date).getDate();
              const isSelected = selectedDate === entry.date;
              const isToday = entry.date === today;
              const isPast = entry.date < today;
              const isTapped = tappedDate === entry.date;
              const isAtHome = entry.type === 'free';
              const isTurnaround = turnaroundDates.has(entry.date);
              const friends = getFriendsInCity(
                entry.type === 'flight' && entry.flight
                  ? entry.flight.arrivalAirport
                  : entry.city,
                entry.date,
                overlaps
              );

              const showCity = entry.type !== 'free';
              const cityLabel =
                entry.type === 'flight' && entry.flight
                  ? entry.flight.arrivalAirport
                  : entry.city;

              const numClass = isSelected ? 'text-accent' : isToday ? 'text-accent' : (entry.type === 'free' && !hasFlights) ? 'text-slate-500' : getCellTextColor(entry.type);

              return (
                <button
                  key={entry.date}
                  onClick={() => handleCellTap(entry.date)}
                  className={`aspect-square rounded-lg flex flex-col items-start pl-[5px] pt-[3px] pr-[3px] pb-[2px] relative transition-all ${
                    isTapped ? 'animate-cell-tap' : ''
                  } ${
                    isSelected
                      ? 'ring-2 ring-accent bg-accent/15 z-[2]'
                      : isToday
                        ? 'ring-2 ring-accent/60'
                        : ''
                  } ${isPast && !isSelected ? 'opacity-55' : ''}`}
                  style={!isSelected && (entry.type !== 'free' || hasFlights) ? { backgroundColor: getCellBg(entry.type) } : undefined}
                >
                  {entry.type === 'flight' && (
                    <div className="absolute left-0 top-1.5 bottom-1.5 w-[2.5px] rounded-full bg-accent" />
                  )}

                  {/* Day number — top left */}
                  <span className={`text-[11px] font-bold leading-none ${numClass}`}>
                    {dayNum}
                  </span>

                  {/* Icon + city code — centered in remaining space */}
                  <div className="flex-1 w-full flex flex-col items-center justify-center">
                    {(entry.type !== 'free' || hasFlights) && (
                      <CellIcon type={entry.type} isTurnaround={isTurnaround} />
                    )}

                    {showCity && (
                      <span className={`font-mono text-[11px] font-bold leading-none mt-px ${getCellCodeColor(entry.type)}`}>
                        {cityLabel}
                      </span>
                    )}
                  </div>

                  {friends.length > 0 && (
                    isAtHome
                      ? <span className="absolute bottom-1 right-1 w-[5px] h-[5px] rounded-full bg-slate-600 opacity-50" />
                      : <span className={`absolute bottom-1 right-1 w-[7px] h-[7px] rounded-full bg-coral ${isPast ? 'opacity-30' : 'animate-pulse-coral'}`} />
                  )}
                  {meetupDateSet.has(entry.date) && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full border-[1.5px] border-coral bg-transparent" />
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {/* Legend */}
        <div className="flex items-center justify-center gap-3 mt-2 pt-1.5 border-t border-slate-700/50">
          <LegendItem color="bg-accent" label="Flight" />
          <LegendItem color="bg-warm-teal" label="Layover" />
          <LegendItem color="bg-sage" label="Free" />
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-coral" />
            <span className="text-xs text-slate-500">Overlap</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full border-[1.5px] border-coral bg-transparent" />
            <span className="text-xs text-slate-500">Meetup</span>
          </div>
        </div>
      </div>

      {/* Unified expandable panel — always present to avoid resize jitter */}
      <div className={`grid-row-expand ${selectedEntry ? '' : 'collapsed'}`}>
        <div>
          {/* Inline detail card for overlap days — flight + friend list */}
          {selectedEntry && selectedOverlapGroup && currentUser && (
            <InlineOverlapDetail
              group={selectedOverlapGroup}
              selectedDate={selectedDate!}
              currentUserHandle={currentUser.handle}
              flight={selectedEntry.type === 'flight' ? selectedEntry.flight : undefined}
              onEditFlight={onEditFlight ? (f) => { haptic(); onEditFlight(f); } : undefined}
              onDeleteFlight={(id) => { removeFlight(id); setSelectedDate(null); }}
              onClose={() => setSelectedDate(null)}
              onPlanMeetup={() => { setNudgeGroup(selectedOverlapGroup); setNudgeInitialDate(selectedDate ?? undefined); }}
              gated={!rosterActive || !!(selectedDate && premiumGatedOverlap?.(selectedDate))}
              premiumGated={!!(selectedDate && premiumGatedOverlap?.(selectedDate)) && !!rosterActive}
              onPremiumTap={() => setShowUpsell(true)}
              isHomeBase={selectedOverlapGroup.city === currentUser.baseAirport || selectedOverlapGroup.myStays.every((s) => s.reason === 'free_at_base')}
              matchCount={selectedEntry.type === 'flight' && selectedEntry.flight ? flightMatchCounts.get(`${selectedEntry.flight.flightNumber}|${selectedEntry.flight.date}`) : undefined}
              onMatchTap={selectedEntry.type === 'flight' && selectedEntry.flight ? () => setFlightMatchFlight(selectedEntry.flight!) : undefined}
            />
          )}

          {/* Meetup cards for selected date */}
          {selectedDateMeetups.map((meetup) => (
            <MeetupInlineCard
              key={meetup.id}
              meetup={meetup}
              rsvps={meetupRsvps.filter((r) => r.meetupId === meetup.id)}
              currentUserId={ctxUser?.id ?? ''}
              usersById={usersById}
              onTap={() => { haptic(); navigate(`/messages?groupChat=${meetup.groupChatId}`); }}
              onRsvp={(vote) => { haptic(); rsvpMeetup(meetup.id, vote); }}
              onCancel={() => { cancelMeetup(meetup.id); }}
            />
          ))}

          {/* Detail panel for non-overlap days (flight/layover/free info) */}
          {selectedEntry && !selectedOverlapGroup && (
            <div className="bg-slate-800 rounded-xl p-3 border border-slate-700/50 mt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-100">
                  {format(parseISO(selectedEntry.date), 'EEE, MMM d')}
                </span>
                <StatusPill type={selectedEntry.type} />
              </div>

              {selectedEntry.type === 'flight' && selectedEntry.flight && (
                <>
                  <FlightDetail
                    flight={selectedEntry.flight}
                    matchCount={flightMatchCounts.get(`${selectedEntry.flight.flightNumber}|${selectedEntry.flight.date}`)}
                    onMatchTap={() => setFlightMatchFlight(selectedEntry.flight!)}
                  />
                  <div className="flex justify-end mt-2 gap-2">
                    {onEditFlight && (
                      <button
                        onClick={() => { haptic(); onEditFlight(selectedEntry.flight!); }}
                        className="flex items-center gap-1.5 text-xs text-accent font-medium px-2.5 py-1 rounded-lg bg-accent/10 hover:bg-accent/20 active:scale-95 transition-all"
                      >
                        <Pencil size={11} />
                        Edit
                      </button>
                    )}
                    {confirmDelete ? (
                      <>
                        <button
                          onClick={() => setConfirmDelete(false)}
                          className="text-xs text-slate-400 px-2.5 py-1 hover:text-slate-200 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            hapticMedium();
                            removeFlight(selectedEntry.flight!.id);
                            setSelectedDate(null);
                          }}
                          className="flex items-center gap-1.5 text-xs text-red-400 font-medium px-2.5 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 active:scale-95 transition-all"
                        >
                          Confirm Remove
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => { haptic(); setConfirmDelete(true); }}
                        className="flex items-center gap-1.5 text-xs text-red-400 font-medium px-2.5 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 active:scale-95 transition-all"
                      >
                        <Trash2 size={11} />
                        Remove
                      </button>
                    )}
                  </div>
                </>
              )}

              {selectedEntry.type === 'layover' && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-warm-teal" />
                  <span className="font-medium text-slate-200">
                    {getCityName(selectedEntry.city)}
                  </span>
                  <span className="text-slate-500 font-mono text-xs">{selectedEntry.city}</span>
                  <span className="text-xs text-slate-500 ml-auto">
                    Day {selectedEntry.layoverDay}/{selectedEntry.layoverTotal}
                  </span>
                </div>
              )}

              {selectedEntry.type === 'free' && (
                <div className="flex items-center gap-2 text-sm">
                  <Coffee size={14} className="text-sage" />
                  <span className="font-medium text-slate-200">Free day</span>
                  <span className="text-slate-500 font-mono text-xs">{selectedEntry.city}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <PremiumUpsellSheet open={showUpsell} onClose={() => setShowUpsell(false)} />
      {nudgeGroup && (
        <NudgeSheet group={nudgeGroup} open={!!nudgeGroup} onClose={() => { setNudgeGroup(null); setNudgeInitialDate(undefined); }} initialDate={nudgeInitialDate} />
      )}
      {flightMatchFlight && (
        <FlightMatchSheet
          open={!!flightMatchFlight}
          onClose={() => setFlightMatchFlight(null)}
          flightNumber={flightMatchFlight.flightNumber}
          date={flightMatchFlight.date}
          count={flightMatchCounts.get(`${flightMatchFlight.flightNumber}|${flightMatchFlight.date}`) ?? 0}
          onShowQR={() => { setFlightMatchFlight(null); setShowQRFromMatch(true); }}
        />
      )}
      {showQRFromMatch && ctxUser && (
        <QRModal
          handle={ctxUser.handle}
          name={ctxUser.name}
          open={showQRFromMatch}
          onClose={() => setShowQRFromMatch(false)}
        />
      )}
    </div>
  );
}

/** Inline meetup card shown in day detail panel */
function MeetupInlineCard({
  meetup, rsvps, currentUserId, usersById, onTap, onRsvp, onCancel,
}: {
  meetup: Meetup;
  rsvps: MeetupRsvp[];
  currentUserId: string;
  usersById: Map<string, User>;
  onTap: () => void;
  onRsvp: (vote: 'yes' | 'no') => void;
  onCancel: () => void;
}) {
  const yesCount = rsvps.filter((r) => r.status === 'yes').length;
  const totalCount = rsvps.length;
  const myRsvp = rsvps.find((r) => r.userId === currentUserId);
  const isConfirmed = meetup.status === 'confirmed';
  const city = getCityName(meetup.city);

  // Face stack — show yes voters first, then pending
  const sortedRsvps = [...rsvps].sort((a, b) => {
    if (a.status === 'yes' && b.status !== 'yes') return -1;
    if (a.status !== 'yes' && b.status === 'yes') return 1;
    return 0;
  });

  function formatTime12(t: string): string {
    const [h] = t.split(':').map(Number);
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  }

  return (
    <button
      onClick={onTap}
      className="mt-2 w-full bg-slate-800 rounded-lg border border-coral/20 p-3 text-left active:scale-[0.98] transition-all"
    >
      <div className="flex items-center gap-2 mb-2">
        <CalendarCheck size={14} className="text-coral shrink-0" />
        <span className="text-sm font-bold text-slate-100 flex-1 truncate">
          {meetup.activity || city}
        </span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          isConfirmed ? 'bg-sage/20 text-sage' : 'bg-coral/15 text-coral'
        }`}>
          {isConfirmed ? 'Confirmed' : 'Proposed'}
        </span>
      </div>

      {/* Time + RSVP count */}
      <div className="flex items-center gap-3 mb-2">
        {meetup.time && (
          <div className="flex items-center gap-1">
            <Clock size={11} className="text-slate-400" />
            <span className="text-xs text-slate-300">{formatTime12(meetup.time)}</span>
          </div>
        )}
        <span className="text-xs text-slate-400">
          {yesCount}/{totalCount} confirmed
        </span>
      </div>

      {/* Face stack */}
      <div className="flex items-center gap-1 mb-2">
        <div className="flex -space-x-1.5">
          {sortedRsvps.slice(0, 5).map((r) => {
            const user = usersById.get(r.userId);
            if (!user) return null;
            const airline = getAirline(user.airlineCode);
            return (
              <div
                key={r.userId}
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold border-[1.5px] shrink-0 overflow-hidden ${
                  r.status === 'yes' ? 'border-sage/60' : r.status === 'no' ? 'border-red-400/60' : 'border-slate-600'
                }`}
                style={{ backgroundColor: airline.color, color: airline.textOnColor }}
              >
                <AvatarImg src={user.avatarUrl} name={user.name} />
              </div>
            );
          })}
          {totalCount > 5 && (
            <span className="text-[10px] text-slate-500 ml-1">+{totalCount - 5}</span>
          )}
        </div>
      </div>

      {/* Quick RSVP buttons if user hasn't responded */}
      {(!myRsvp || myRsvp.status === 'pending') && (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => { e.stopPropagation(); onRsvp('yes'); }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold bg-sage/15 text-sage border border-sage/30 active:scale-95 transition-all"
          >
            <ThumbsUp size={10} /> Yes
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRsvp('no'); }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20 active:scale-95 transition-all"
          >
            <ThumbsDown size={10} /> No
          </button>
        </div>
      )}

      {/* Cancel button */}
      <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={(e) => { e.stopPropagation(); hapticMedium(); onCancel(); }}
          className="flex items-center justify-center gap-1 w-full py-1 rounded-lg text-[10px] font-medium text-slate-500 hover:text-red-400 active:scale-95 transition-all"
        >
          <X size={9} />
          Cancel
        </button>
      </div>
    </button>
  );
}

/** Find the CityOverlapGroup that contains the given date.
 *  Prioritises layover/trip groups over home-base groups.
 *  Home-base groups only match when a friend is present that day,
 *  and can be skipped entirely on flight days (skipHome).
 *  Uses baseAirport for reliable home detection (myStay.reason is
 *  unreliable because flight departure days get reason:'layover'). */
function findOverlapGroupForDate(
  date: string,
  groups: CityOverlapGroup[],
  skipHome = false,
  baseAirport?: string,
): CityOverlapGroup | null {
  const isHome = (g: CityOverlapGroup) =>
    (baseAirport && g.city === baseAirport) ||
    g.myStays.every((s) => s.reason === 'free_at_base');

  // 1. Non-home (layover/trip) groups — exact match on myStay range
  for (const g of groups) {
    if (isHome(g)) continue;
    for (const stay of g.myStays) {
      if (date >= stay.startDate && date <= stay.endDate) return g;
    }
  }
  // 2. Home-base groups — only if a friend is actually present on this date.
  //    Skipped on flight days so the flight detail panel shows instead.
  if (skipHome) return null;
  for (const g of groups) {
    if (!isHome(g)) continue;
    const hasFriendOnDay = g.friendStays.some(
      (fs) => date >= fs.startDate && date <= fs.endDate,
    );
    if (!hasFriendOnDay) continue;
    for (const stay of g.myStays) {
      if (date >= stay.startDate && date <= stay.endDate) return g;
    }
  }
  return null;
}

function getCellBg(type: RosterEntry['type']): string {
  return {
    flight: 'rgba(196, 149, 106, 0.12)',
    layover: 'rgba(91, 158, 158, 0.12)',
    free: 'rgba(138, 159, 122, 0.10)',
  }[type];
}

function getCellTextColor(type: RosterEntry['type']): string {
  return {
    flight: 'text-accent',
    layover: 'text-warm-teal',
    free: 'text-sage/70',
  }[type];
}

function getCellCodeColor(type: RosterEntry['type']): string {
  return {
    flight: 'text-accent/80',
    layover: 'text-warm-teal/80',
    free: 'text-slate-400',
  }[type];
}

function CellIcon({ type, isTurnaround }: { type: RosterEntry['type']; isTurnaround?: boolean }) {
  if (isTurnaround) {
    return (
      <div className="flex items-center gap-0">
        <Plane size={14} className="-rotate-45 text-accent" />
        <Plane size={14} className="rotate-[135deg] text-accent/60" />
      </div>
    );
  }
  if (type === 'flight') return <Plane size={16} className="text-accent" />;
  if (type === 'layover') return <MapPin size={16} className="text-warm-teal" />;
  return <Home size={14} className="text-sage/40" />;
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className={`w-2.5 h-2.5 rounded-sm ${color} opacity-60`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
  );
}

function StatusPill({ type }: { type: RosterEntry['type'] }) {
  const config = {
    flight: { label: 'Flight', bg: 'bg-accent/20', text: 'text-accent' },
    layover: { label: 'Layover', bg: 'bg-warm-teal/20', text: 'text-warm-teal' },
    free: { label: 'Free', bg: 'bg-sage/20', text: 'text-sage' },
  }[type];
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function FlightDetail({ flight, matchCount, onMatchTap }: {
  flight: NonNullable<RosterEntry['flight']>;
  matchCount?: number;
  onMatchTap?: () => void;
}) {
  const airline = getAirline(flight.airlineCode);
  const isNextDay = !!(flight.departureTime && flight.arrivalTime &&
    flight.arrivalTime < flight.departureTime);
  return (
    <div className="rounded-lg px-3 py-2 bg-slate-700/30">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: airline.color }}>
          <Plane size={10} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-100 font-mono">{flight.flightNumber}</span>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: airline.color, color: airline.textOnColor }}
            >
              {airline.name}
            </span>
            {matchCount && matchCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); haptic(); onMatchTap?.(); }}
                className="flex items-center gap-0.5 text-[10px] font-semibold text-coral bg-coral/10 px-1.5 py-0.5 rounded-full active:scale-95 transition-all"
              >
                <Users size={9} />
                {matchCount} crew
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-xs text-slate-300 font-mono">{flight.departureAirport}</span>
            {flight.departureTime && (
              <span className="text-[11px] text-slate-400 font-mono">{flight.departureTime}</span>
            )}
            <span className="text-slate-600 mx-0.5">{'\u2192'}</span>
            <span className="text-xs text-slate-300 font-mono">{flight.arrivalAirport}</span>
            {flight.arrivalTime && (
              <span className="text-[11px] text-slate-400 font-mono">
                {flight.arrivalTime}{isNextDay && <sup className="text-[9px] text-slate-500 ml-px">+1</sup>}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Inline overlap detail for calendar day tap — clean friend list instead of Gantt */
function InlineOverlapDetail({
  group, selectedDate, currentUserHandle, flight, onEditFlight, onDeleteFlight, onClose, onPlanMeetup,
  gated, premiumGated, onPremiumTap, isHomeBase, matchCount, onMatchTap,
}: {
  group: CityOverlapGroup;
  selectedDate: string;
  currentUserHandle: string;
  flight?: Flight;
  onEditFlight?: (f: Flight) => void;
  onDeleteFlight?: (id: string) => void;
  onClose: () => void;
  onPlanMeetup: () => void;
  gated: boolean;
  premiumGated: boolean;
  onPremiumTap: () => void;
  isHomeBase: boolean;
  matchCount?: number;
  onMatchTap?: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [friendProfileId, setFriendProfileId] = useState<string | null>(null);
  const [chatTarget, setChatTarget] = useState<{ partnerId: string; prefill?: string } | null>(null);

  // Deduplicate friends by user ID, merge stays
  // For home base groups, only show friends actually present on the selected date
  const friendsByUser = new Map<string, FriendStay[]>();
  for (const fs of group.friendStays) {
    if (isHomeBase && (selectedDate < fs.startDate || selectedDate > fs.endDate)) continue;
    const existing = friendsByUser.get(fs.user.id);
    if (existing) existing.push(fs);
    else friendsByUser.set(fs.user.id, [fs]);
  }
  const friendEntries = [...friendsByUser.entries()];

  const city = getCityName(group.city);

  return (
    <div className="mt-2 bg-slate-800 rounded-lg border border-coral/30 p-3">
      {/* City header + close */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {isHomeBase ? <Home size={14} className="text-slate-400" /> : <MapPin size={14} className="text-accent" />}
          <span className="font-bold text-slate-100 text-sm">{city}</span>
          <span className="text-xs font-mono text-slate-500">{group.city}</span>
          {isHomeBase && (
            <span className="text-[9px] font-bold text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded uppercase tracking-wide">Home</span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-lg text-slate-500 hover:text-slate-300 active:scale-90 transition-all">
          <X size={14} />
        </button>
      </div>

      {/* Flight detail */}
      {flight && (
        <div className="mb-2 relative">
          <FlightDetail flight={flight} matchCount={matchCount} onMatchTap={onMatchTap} />
          <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
            {onEditFlight && !confirmDelete && (
              <button onClick={() => { haptic(); onEditFlight(flight); }} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 active:scale-90 transition-all">
                <Pencil size={12} />
              </button>
            )}
            {confirmDelete ? (
              <>
                <button onClick={() => setConfirmDelete(false)} className="text-[11px] text-slate-500 px-1.5 py-0.5">Cancel</button>
                <button onClick={() => { hapticMedium(); onDeleteFlight?.(flight.id); }} className="text-[11px] text-red-400 font-medium px-1.5 py-0.5">Confirm</button>
              </>
            ) : onDeleteFlight && (
              <button onClick={() => { haptic(); setConfirmDelete(true); }} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 active:scale-90 transition-all">
                <Trash2 size={12} />
              </button>
            )}
          </div>
          <div className="border-t border-slate-700/50 mt-2 mb-1" />
        </div>
      )}

      {/* Friends header */}
      <div className="text-[10px] font-bold text-coral uppercase tracking-wider mb-2">
        Friends in {city}
      </div>

      {/* Friend list */}
      {gated ? (
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <span>{friendEntries.length} crew member{friendEntries.length !== 1 ? 's' : ''} here</span>
          {premiumGated ? (
            <button onClick={onPremiumTap} className="inline-flex items-center gap-1 text-amber-400 font-semibold active:opacity-70">
              <Crown size={10} />
              Unlock with Premium
            </button>
          ) : (
            <span>— Add your flights to reveal names</span>
          )}
        </div>
      ) : (
        <div className="flex flex-col">
          {friendEntries.map(([userId, stays]) => {
            const fs = stays[0];
            const airline = getAirline(fs.airlineCode);
            const overlapStart = stays.reduce((min, s) => s.overlapWithMe.startDate < min ? s.overlapWithMe.startDate : min, stays[0].overlapWithMe.startDate);
            const overlapEnd = stays.reduce((max, s) => s.overlapWithMe.endDate > max ? s.overlapWithMe.endDate : max, stays[0].overlapWithMe.endDate);
            const totalDays = stays.reduce((sum, s) => sum + s.overlapWithMe.days, 0);
            const arrivesLater = overlapStart > selectedDate;
            const isLocal = isHomeBase && fs.user.baseAirport === group.city;

            return (
              <div key={userId} className="flex items-center gap-2.5 py-1.5 border-t border-slate-700/30 first:border-t-0">
                <button
                  onClick={() => { haptic(); setFriendProfileId(userId); }}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 overflow-hidden active:opacity-70"
                  style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                >
                  <AvatarImg src={fs.user.avatarUrl} name={fs.user.name} />
                </button>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => { haptic(); setFriendProfileId(userId); }}
                    className="text-xs font-semibold text-slate-100 active:opacity-70"
                  >
                    {fs.user.name}
                  </button>
                  <div className="text-[10px] text-slate-500">
                    {airline.name} &middot; {formatDateShort(overlapStart)} &ndash; {formatDateShort(overlapEnd)}
                  </div>
                  {arrivesLater && (
                    <div className="text-[9px] text-coral font-semibold">Arrives {formatDateShort(overlapStart)}</div>
                  )}
                </div>
                <button
                  onClick={() => { haptic(); setChatTarget({ partnerId: userId, prefill: `Hey! Saw we overlap in ${city}. Want to meet up?` }); }}
                  className="shrink-0 active:scale-90 transition-transform p-1"
                >
                  <MessageCircle size={13} className="text-accent" />
                </button>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${
                  isLocal
                    ? 'bg-slate-700/40 text-slate-500'
                    : arrivesLater
                      ? 'bg-coral/10 text-coral'
                      : 'bg-sage/10 text-sage'
                }`}>
                  {isLocal ? 'Local' : `${totalDays}d`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons — Share + Plan Meetup */}
      {!gated && friendEntries.length > 0 && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={async () => {
              haptic();
              const cityName = getCityName(group.city);
              const dateRange = group.rangeStart === group.rangeEnd
                ? formatDateShort(group.rangeStart)
                : `${formatDateShort(group.rangeStart)}-${formatDateShort(group.rangeEnd)}`;
              const text = `Hey! I'm in ${cityName} ${dateRange}. Let's meet up!\n\nAdd me: rostandfound.app/add/${currentUserHandle}\nDownload: ${DOWNLOAD_URL}`;
              const result = await shareText({ text });
              if (result !== 'cancelled') {
                track('overlap_shared', { city: group.city, method: result === 'shared' ? 'native' : 'clipboard' });
              }
            }}
            className="flex-1 py-2 rounded-lg bg-slate-700/50 border border-slate-600/50 text-slate-300 text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] active:bg-slate-700 transition-all"
          >
            <Share2 size={13} />
            Share
          </button>
          <button
            onClick={() => { haptic(); onPlanMeetup(); }}
            className="flex-1 py-2 rounded-lg bg-coral/15 border border-coral/30 text-coral text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] active:bg-coral/25 transition-all"
          >
            <Users size={13} />
            Plan Meetup
          </button>
        </div>
      )}

      {friendProfileId && <FriendProfileSheet friendId={friendProfileId} onClose={() => setFriendProfileId(null)} />}
      {chatTarget && <ChatView partnerId={chatTarget.partnerId} prefill={chatTarget.prefill} onClose={() => setChatTarget(null)} />}
    </div>
  );
}
